import { Box, Chip, Stack, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { proxied } from "../lib/imageProxy.js";

export interface HeroChip {
  label: string;
  color?: "default" | "primary" | "white";
  icon?: ReactNode;
}

interface Props {
  imageUrl?: string | null;
  /** Small uppercase eyebrow text above the title (e.g. "RIG OF THE WEEK"). */
  eyebrow?: string | null;
  /** Soft pre-title (brand or supertitle), shown above the main title. */
  brand?: string | null;
  /** The big h1. */
  title: string;
  /** Optional line below the title, e.g. "Spotted on Heartland Kubota". */
  subtitle?: string | null;
  /** Floating chips top-left of the hero. */
  chips?: HeroChip[];
  /** Optional content rendered below the title block. */
  meta?: ReactNode;
  /** Aspect ratio (e.g. "21 / 9", "16 / 9"). Default 21/9. */
  aspectRatio?: string;
  /** When true, eager-load the image (above-the-fold hero). Default true. */
  eager?: boolean;
}

/**
 * Cinematic hero used at the top of machine-centric pages. Full-bleed (this
 * component does not constrain its own width — let the page or layout
 * decide). Falls back to a warm gradient banner when no image.
 */
export function MachineHero({
  imageUrl,
  eyebrow,
  brand,
  title,
  subtitle,
  chips,
  meta,
  aspectRatio = "21 / 9",
  eager = true,
}: Props) {
  const chipBgFor = (color: HeroChip["color"]) => {
    if (color === "primary") {
      return { bgcolor: "primary.main", color: "common.black", fontWeight: 700 };
    }
    if (color === "white") {
      return { bgcolor: "rgba(255,255,255,0.95)", color: "common.black", fontWeight: 600 };
    }
    return {
      bgcolor: "rgba(0,0,0,0.6)",
      color: "common.white",
      backdropFilter: "blur(6px)",
      border: "1px solid rgba(255,255,255,0.12)",
    };
  };

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        bgcolor: "background.paper",
        boxShadow: "0 1px 0 rgba(255,255,255,0.05) inset, 0 24px 60px -24px rgba(0,0,0,0.6)",
      }}
    >
      {imageUrl ? (
        <Box
          sx={{
            position: "relative",
            aspectRatio: { xs: "16 / 10", md: aspectRatio },
            width: "100%",
          }}
        >
          <Box
            component="img"
            src={proxied(imageUrl)}
            alt={title}
            loading={eager ? "eager" : "lazy"}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              filter: "saturate(1.05) contrast(1.02)",
            }}
          />
          {/* gradient wash for readable text */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.0) 30%, rgba(0,0,0,0.65) 75%, rgba(0,0,0,0.94) 100%)",
              pointerEvents: "none",
            }}
          />
          {/* neon amber accent stripe at bottom */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              bottom: 0,
              width: "100%",
              height: 4,
              background: "linear-gradient(90deg, #ffb000 0%, #ff8800 50%, transparent 100%)",
              boxShadow:
                "0 0 12px rgba(255,176,0,0.85), 0 0 28px rgba(255,176,0,0.55), 0 -2px 14px rgba(255,176,0,0.4)",
            }}
          />
          {chips && chips.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: "absolute",
                top: { xs: 12, md: 20 },
                left: { xs: 12, md: 24 },
                flexWrap: "wrap",
                gap: 1,
                maxWidth: "calc(100% - 32px)",
              }}
            >
              {chips.map((c, i) => (
                <Chip
                  key={i}
                  size="small"
                  icon={c.icon as React.ReactElement}
                  label={c.label}
                  sx={chipBgFor(c.color)}
                />
              ))}
            </Stack>
          )}
          <Box
            sx={{
              position: "absolute",
              left: { xs: 16, md: 32 },
              right: { xs: 16, md: 32 },
              bottom: { xs: 20, md: 36 },
              color: "common.white",
            }}
          >
            {eyebrow && (
              <Typography
                variant="overline"
                sx={{
                  color: "primary.main",
                  letterSpacing: 3,
                  fontWeight: 700,
                  lineHeight: 1,
                  display: "inline-block",
                  textShadow:
                    "0 0 8px rgba(255,176,0,0.9), 0 0 18px rgba(255,176,0,0.55), 0 0 32px rgba(255,176,0,0.35)",
                }}
              >
                {eyebrow}
              </Typography>
            )}
            {brand && (
              <Typography
                variant="h5"
                sx={{ mt: 1, fontWeight: 400, opacity: 0.85 }}
              >
                {brand}
              </Typography>
            )}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: 32, sm: 44, md: 60 },
                fontWeight: 900,
                lineHeight: 1,
                mt: brand ? 0.5 : 1,
                textShadow: "0 2px 20px rgba(0,0,0,0.4)",
                letterSpacing: "-0.02em",
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body1" sx={{ mt: 2, opacity: 0.92, fontSize: { xs: 16, md: 18 } }}>
                {subtitle}
              </Typography>
            )}
            {meta}
          </Box>
        </Box>
      ) : (
        // Image-less fallback — warm gradient + same typographic structure
        <Box
          sx={{
            p: { xs: 4, md: 7 },
            background:
              "linear-gradient(135deg, rgba(255,176,0,0.18) 0%, rgba(255,136,0,0.04) 60%, transparent 100%)",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {eyebrow && (
            <Typography
              variant="overline"
              sx={{ color: "primary.main", letterSpacing: 3, fontWeight: 700 }}
            >
              {eyebrow}
            </Typography>
          )}
          {brand && (
            <Typography variant="h5" color="text.secondary" sx={{ mt: 1, fontWeight: 400 }}>
              {brand}
            </Typography>
          )}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: 32, md: 56 },
              fontWeight: 900,
              mt: brand ? 0.5 : 1,
              letterSpacing: "-0.02em",
              lineHeight: 1.05,
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, fontSize: 18 }}>
              {subtitle}
            </Typography>
          )}
          {chips && chips.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 2.5, flexWrap: "wrap", gap: 1 }}>
              {chips.map((c, i) => (
                <Chip key={i} size="small" label={c.label} icon={c.icon as React.ReactElement} />
              ))}
            </Stack>
          )}
          {meta && <Box sx={{ mt: 2 }}>{meta}</Box>}
        </Box>
      )}
    </Box>
  );
}
